import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import { AppConfig } from '../../../environment/app-config.model';
import { DatoCmsNewsletterSubscription } from '../../../schema/datocms/blocks';
import { getButtonClass, getTextAlignmentClass } from '../../../utilities';
import { EMAIL_REGEX } from '../../../utilities/constants';
import HtmlStringComponent from '../../html-string/html-string-component';
import { updateDataLayer } from '../../../utilities';
import Icon from '../../icon/icon';
import { DatalayerEvents } from '../../../schema/enum/datalayer-events';

interface SubscriptionInfo {
    emailAddress: string;
    emailError: string;
    emailTouched: boolean;
}

interface NewsletterSubscriptionRequest {
    email: string;
    externalCustomerId?: string;
    token: string;
    subscriptionState: 'Subscribed' | 'Unsubscribed';
}

export const EnvironmentStaticQuery = graphql`query EnvironmentStaticQuery {
    environment: environmentJson {
        bhtpConsumerGatewayBaseUrl
        recaptchaSiteKey
    }
}`;

interface EnvironmentStaticQueryResult {
    environment: Partial<AppConfig>;
}

const NewsLetterSubscriptionComponent = ({ title, titleAlignment, descriptionHtml, emailAddressPlaceholder, emailFormatMessage, emailRequiredMessage, submitButtonText, buttonStyleOverride, includeBackground, componentId }: (DatoCmsNewsletterSubscription & { includeBackground?: boolean })): React.ReactElement => {
    const { environment } = useStaticQuery<EnvironmentStaticQueryResult>(EnvironmentStaticQuery);

    const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({
        emailError: emailRequiredMessage,
        emailTouched: false,
        emailAddress: ''
    });

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successful, setSuccessful] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    function handleEmailChanged(event: React.ChangeEvent<HTMLInputElement>): void {
        const email: string = event.target.value;
        let error: string = '';
        if (!email) {
            error = emailRequiredMessage;
        } else if (!EMAIL_REGEX.test(email)) {
            error = emailFormatMessage;
        }

        const updatedSubscriptionInfo = {
            ...subscriptionInfo,
            emailError: error,
            emailAddress: email
        };

        setSubscriptionInfo(updatedSubscriptionInfo);
    }

    function handleEmailBlur(event: React.FocusEvent<HTMLInputElement>): void {
        const updatedSubscriptionInfo = {
            ...subscriptionInfo,
            emailTouched: true
        };

        setSubscriptionInfo(updatedSubscriptionInfo);
    }

    function publishSignupEvent(): void {
        const eventModel = {
            method: 'newsletter'
        };
        updateDataLayer(DatalayerEvents.Sign_Up, eventModel);
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        const updatedSubscriptionInfo = {
            ...subscriptionInfo,
            emailTouched: true
        };

        setSubscriptionInfo(updatedSubscriptionInfo);

        if (!subscriptionInfo.emailError) {
            setSubmitting(true);

            grecaptcha.ready(() => {
                grecaptcha.execute(environment.recaptchaSiteKey || '', { action: 'newsletter_sign_up'})
                    .then((token: string) => {
                        const req: NewsletterSubscriptionRequest = {
                            email: subscriptionInfo.emailAddress,
                            subscriptionState: 'Subscribed',
                            token
                        };

                        fetch(`${environment.bhtpConsumerGatewayBaseUrl}customer/signup/newsletter`, {
                            method: 'POST',
                            cache: 'no-cache',
                            headers: {
                                'content-type': 'application/json'
                            },
                            body: JSON.stringify(req)
                        }).then((resp) => {
                            const success: boolean = resp.ok;
                            setSubmitting(false);
                            setSuccessful(success);
                            setHasError(!success);
                            publishSignupEvent();
                        }).catch(() => {
                            setSubmitting(false);
                            setHasError(true);
                        });
                    });
            });
        }
    }

    const wrapperClasses: string = includeBackground ? ' py-8 px-24 bg-brand-light-gray border rounded-none md:rounded-2xl border-brand-secondary' : '';
    const form = (
        <div id={componentId} className={`space-y-4 ${wrapperClasses}`}>
            { title && <h3 className={`text-center md:${getTextAlignmentClass(titleAlignment)}`}>{title}</h3>}

            { descriptionHtml && <HtmlStringComponent htmlString={descriptionHtml} skipAlignmentClasses={true} /> }

            { successful && <span>Thanks! We&apos;ve received your email address.</span> }

            { hasError && <span className="text-error">Sorry, there was an unknown error. Please try again later.</span> }

            { !successful && <form onSubmit={handleSubmit} id="newsletterForm" name="newsletterForm" className="flex flex-col lg:flex-row items-center lg:items-start space-x-0 lg:space-x-4 space-y-4 lg:space-y-0">
                { submitting && <div className="flex justify-center items-center space-x-2 text-primary"><Icon name="data_saver_off" className="fill-current animate-spin w-8 h-8" /><span>Submitting</span></div> }

                { !submitting && <div className="flex flex-col space-y-1">
                    <input type="text"
                        name="emailAddress"
                        id="newsletter-email-address"
                        className={`bordered-input w-72 lg:w-96 ${subscriptionInfo.emailError && subscriptionInfo.emailTouched ? 'input-with-error' : ''}`}
                        placeholder={emailAddressPlaceholder}
                        title={emailAddressPlaceholder}
                        onChange={handleEmailChanged}
                        onBlur={handleEmailBlur} />

                    { subscriptionInfo.emailError && subscriptionInfo.emailTouched && <div className="input-error-message">{subscriptionInfo.emailError}</div> }
                </div> }

                { !submitting && <button type="submit" disabled={submitting} className={`${getButtonClass(buttonStyleOverride)}`}>{submitButtonText}</button> }
            </form>}
            { (!successful && !submitting) && <p className="text-brand-gray text-xs">This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.</p> }
        </div>
    );

    return form;
};

export default NewsLetterSubscriptionComponent;
